import { format } from 'date-fns'
import { PROPERTY_TYPES_COLORS } from '~/constants'
import { META_FIELDS, type Listing, type MetaFieldConfig } from '~/models'

export function getPropertyTypeClassName(type: string) {
  let bdgColor = PROPERTY_TYPES_COLORS.find(
    (config) => config.propertyType === type
  )
  return bdgColor?.className
}

export function snakeCaseToTitleCase(s: string) {
  const result = s
    .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase()) // Initial char (after -/_)
    .replace(/[-_]+(.)/g, (_, c) => ' ' + c.toUpperCase())
  return result
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

function getMetaValue(
  value: any,
  metaConfig: MetaFieldConfig,
  meta: {
    [key: string]: any
  },
  valuesOnly = false
) {
  if (valuesOnly) {
    return typeof metaConfig.format === 'function'
      ? metaConfig.format(value, meta, valuesOnly)
      : value
  }
  if (metaConfig.format === 'currency') {
    value = currencyFormatter.format(value).replace('.00', '')
  } else if (typeof metaConfig.format === 'function') {
    value = metaConfig.format(value, meta, valuesOnly)
  } else if (value === true) {
    return 'Yes'
  } else if (
    metaConfig.format === 'number' ||
    metaConfig.suffix ||
    metaConfig.prefix
  ) {
    // Assuming anything with prefix/suffix will be a number, may have to change
    value = value.toLocaleString()
  }
  if (metaConfig.prefix) {
    value = `${metaConfig.prefix} ${value}`
  }
  if (metaConfig.suffix) {
    value =
      metaConfig.suffix === '%' ? `${value}%` : `${value} ${metaConfig.suffix}`
  }
  return value
}

export function getMetaLabelValues(
  meta: {
    [key: string]: any
  },
  valuesOnly = false
): { key: string; label: string; value: string }[] {
  return Object.keys(META_FIELDS)
    .filter(
      (key) =>
        META_FIELDS[key].render !== false &&
        (!!meta[key] || META_FIELDS[key].generated)
    )
    .map((key) => {
      const metaConfig = META_FIELDS[key]
      const value = getMetaValue(meta[key] || '', metaConfig, meta, valuesOnly)
      return {
        key,
        label: metaConfig.label ? metaConfig.label : snakeCaseToTitleCase(key),
        value,
      }
    })
    .filter(({ value }) => value)
}

export function getLeaseMetaValues(
  listing: Listing,
  valuesOnly = false
): { key: string; label: string; value: string }[] {
  const values: { key: string; label: string; value: string }[] = []
  if (!listing.suites || listing.suites.length === 0) {
    return []
  } else if ((listing.suites || []).length > 0) {
    const rateTypeLabels: any = {
      sf_year: '/SF/Year',
      month: '/Month',
      year: '/Year',
      sf_month: '/SF/Month',
    }
    const rate_min =
      listing.suites.length === 1
        ? listing.suites![0].rate_min
        : listing.meta.min_lease_rate
    const rate_max =
      listing.suites.length === 1
        ? listing.suites![0].rate_max
        : listing.meta.max_lease_rate
    const max_available_sf =
      listing.suites.length === 1
        ? listing.suites![0].max_available_sf
        : listing.meta.max_available_sf
    const min_available_sf =
      listing.suites.length === 1
        ? listing.suites![0].min_available_sf
        : listing.meta.min_available_sf

    const rateType =
      listing.suites!.length === 1
        ? rateTypeLabels[listing.suites![0].rate_type!]
        : 'SF/year'

    if (rate_min && rate_max && rate_min !== rate_max) {
      values.push({
        key: 'leaseRate',
        label: 'Lease Rate',
        value: valuesOnly
          ? `${rate_min} - ${rate_max}`
          : `$${rate_min.toLocaleString()} - $${rate_max.toLocaleString()} ${rateType}`,
      })
    } else if (rate_min) {
      values.push({
        key: 'leaseRate',
        label: 'Lease Rate',
        value: valuesOnly
          ? rate_min.toString()
          : `$${rate_min.toLocaleString()} ${rateType}`,
      })
    }
    if (
      min_available_sf &&
      max_available_sf &&
      min_available_sf !== max_available_sf
    ) {
      values.push({
        key: 'availableSf',
        label: 'Available SF',
        value: valuesOnly
          ? `${min_available_sf} - ${max_available_sf}`
          : `${min_available_sf.toLocaleString()} - ${max_available_sf.toLocaleString()} SF`,
      })
    } else if (min_available_sf) {
      values.push({
        key: 'availableSf',
        label: 'Available SF',
        value: valuesOnly
          ? min_available_sf.toString()
          : `${min_available_sf.toLocaleString()} SF`,
      })
    }
  }
  return values
}

export const getListingTypeLabel = (listing: Listing) => {
  const labels: { [key: string]: string } = {
    sale: 'For Sale',
    lease: 'For Lease',
    'sale/lease': 'For Sale/Lease',
  }
  if (listing.status === 'leased') {
    return 'Fully Leased'
  } else if (listing.status === 'sold') {
    return 'Sold'
  }
  return labels[listing.type]
}

export function getReportMetaLabelValues(listing: Listing) {
  const leaseMeta = getLeaseMetaValues(listing)
  const availableSpaces =
    (listing.suites || []).length > 1
      ? [
          {
            key: 'availableSpaces',
            label: 'Available Spaces',
            value: listing
              .suites!.filter((suites) => suites.available)
              .length.toString(),
          },
        ]
      : []

  const metaLabelValues = [
    ...availableSpaces,
    ...(leaseMeta.length > 0 ? leaseMeta : []),
    ...getMetaLabelValues(listing.meta),
    {
      key: 'created',
      label: 'Created',
      value: format(listing.created, 'MM/dd/yyyy'),
    },
    {
      key: 'modified',
      label: 'Updated',
      value: format(listing.modified, 'MM/dd/yyyy'),
    },
  ]
  return [...metaLabelValues]
}
